import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Settings, LogOut, ChevronRight } from "lucide-react";
import { AppDispatch, persistor } from "../../../store/store";
import { logout } from "../../../store/authSlice";
import { adminLogout } from "../../../services/api";
import { primaryNav, isNavActive } from "./nav-config";
import { cn } from "../../../lib/util";

export function Sidebar() {
  const { pathname } = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [openSection, setOpenSection] = useState<string | null>(null);

  // Keep the section that matches the current route expanded.
  useEffect(() => {
    const active = primaryNav.find(
      (item) => item.children && isNavActive(pathname, item)
    );
    if (active) setOpenSection(active.name);
  }, [pathname]);

  const handleLogout = async () => {
    try {
      await adminLogout();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      dispatch(logout());
      persistor.purge();
      navigate("/");
    }
  };

  const settingsActive = pathname === "/control/settings" || pathname.startsWith("/control/settings/");

  return (
    <aside className="fixed left-0 top-0 z-30 hidden h-screen w-[264px] flex-col bg-[#B87775] text-white lg:flex">
      {/* Wordmark */}
      <div className="flex h-20 items-center px-6">
        <Link to="/control" className="flex items-center" aria-label="Charme admin home">
          <img src="/images/whiteLogo.svg" alt="Charme" className="h-9" />
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4" aria-label="Primary">
        <p className="px-3 pb-2 pt-1 text-[10px] font-medium uppercase tracking-[0.22em] text-white/55">
          Run the studio
        </p>
        <ul className="space-y-0.5">
          {primaryNav.map((item) => {
            const active = isNavActive(pathname, item);
            const expanded = openSection === item.name;
            const Icon = item.icon;
            return (
              <li key={item.name}>
                {item.children ? (
                  <button
                    type="button"
                    onClick={() =>
                      setOpenSection((prev) => (prev === item.name ? null : item.name))
                    }
                    aria-expanded={expanded}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70",
                      active ? "bg-white/20 font-medium" : "hover:bg-black/15"
                    )}
                  >
                    <Icon className="h-[18px] w-[18px] shrink-0" />
                    <span className="flex-1 text-left">{item.name}</span>
                    <ChevronRight
                      className={cn(
                        "h-4 w-4 transition-transform",
                        expanded && "rotate-90"
                      )}
                    />
                  </button>
                ) : (
                  <Link
                    to={item.href}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70",
                      active ? "bg-white/20 font-medium" : "hover:bg-black/15"
                    )}
                  >
                    <Icon className="h-[18px] w-[18px] shrink-0" />
                    <span className="flex-1">{item.name}</span>
                    {active && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                  </Link>
                )}

                {item.children && expanded && (
                  <ul className="mb-1 ml-4 mt-0.5 space-y-0.5 border-l border-white/20 pl-3">
                    {item.children.map((child) => {
                      const childActive =
                        pathname === child.href || pathname.startsWith(child.href + "/");
                      const ChildIcon = child.icon;
                      return (
                        <li key={child.href}>
                          <Link
                            to={child.href}
                            aria-current={childActive ? "page" : undefined}
                            className={cn(
                              "flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70",
                              childActive
                                ? "bg-white/15 font-medium text-white"
                                : "text-white/85 hover:bg-black/15"
                            )}
                          >
                            <ChildIcon className="h-4 w-4 shrink-0" />
                            {child.name}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Configuration + session */}
      <div className="border-t border-white/15 px-3 py-3">
        <Link
          to="/control/settings"
          aria-current={settingsActive ? "page" : undefined}
          className={cn(
            "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70",
            settingsActive ? "bg-white/20 font-medium" : "hover:bg-black/15"
          )}
        >
          <Settings className="h-[18px] w-[18px]" />
          Settings
        </Link>
        <button
          type="button"
          onClick={handleLogout}
          className="mt-0.5 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-white/85 transition-colors hover:bg-black/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          <LogOut className="h-[18px] w-[18px]" />
          Sign out
        </button>
      </div>
    </aside>
  );
}
